import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Mic, PenTool, BookOpen, Sparkles, ArrowRight } from "lucide-react";
import { API } from "@/App";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function LandingPage() {
  const navigate = useNavigate();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        // Not authenticated, stay on landing
      } finally {
        setIsCheckingAuth(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse-glow w-4 h-4 rounded-full bg-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-overlay">
      {/* Hero Section */}
      <section className="hero-bg min-h-screen flex flex-col">
        {/* Navigation */}
        <nav className="relative z-10 flex items-center justify-between p-6 md:p-8">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_laughcrafter/artifacts/c6aabxrp_ITM_Logo_1000px.png" 
              alt="BitStack" 
              className="h-10 md:h-12 w-auto"
            />
            <span className="font-display text-xl uppercase tracking-wider text-foreground hidden sm:inline">
              BitStack
            </span>
          </div>
          <Button 
            onClick={handleLogin}
            data-testid="nav-login-btn"
            className="bg-primary text-primary-foreground font-bold uppercase tracking-wider hover:bg-spotlight-hover"
          >
            Sign In
          </Button>
        </nav>

        {/* Hero Content */}
        <div className="flex-1 flex items-center justify-center px-6 md:px-12">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight text-foreground mb-6">
              Write <span className="gradient-text">Killer</span> Material
            </h1>
            <p className="font-sans text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              Your personal comedy writing studio. Craft, organize, and refine your jokes 
              with AI-powered brainstorming that respects your unique voice.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={handleLogin}
                size="lg"
                data-testid="hero-get-started-btn"
                className="bg-primary text-primary-foreground font-bold uppercase tracking-wider text-lg px-8 py-6 hover:bg-spotlight-hover spotlight-glow-hover btn-lift"
              >
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="relative z-10 pb-8 flex justify-center">
          <div className="animate-bounce">
            <div className="w-1 h-8 rounded-full bg-gradient-to-b from-primary to-transparent" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 brick-overlay">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight text-center mb-4">
            Your Comedy <span className="gradient-text">Toolkit</span>
          </h2>
          <p className="text-muted-foreground text-center mb-16 max-w-xl mx-auto">
            Everything you need to write, refine, and perfect your stand-up material.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Feature 1 */}
            <div className="glass p-6 rounded-sm animate-fade-in stagger-1 opacity-0" style={{animationFillMode: 'forwards'}}>
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                <PenTool className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl uppercase mb-2">Joke Library</h3>
              <p className="text-muted-foreground text-sm">
                Organize your material with premise, setup, punchline structure. Tag and categorize everything.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="glass p-6 rounded-sm animate-fade-in stagger-2 opacity-0" style={{animationFillMode: 'forwards'}}>
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl uppercase mb-2">AI Brainstorm</h3>
              <p className="text-muted-foreground text-sm">
                Multiple AI engines suggest tweaks to your jokes. Your voice, amplified. Coming soon.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="glass p-6 rounded-sm animate-fade-in stagger-3 opacity-0" style={{animationFillMode: 'forwards'}}>
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                <Mic className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl uppercase mb-2">Voice Capture</h3>
              <p className="text-muted-foreground text-sm">
                Record ideas on the go. Whisper transcribes your thoughts instantly. Coming soon.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="glass p-6 rounded-sm animate-fade-in stagger-4 opacity-0" style={{animationFillMode: 'forwards'}}>
              <div className="w-12 h-12 rounded-sm bg-primary/10 flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-xl uppercase mb-2">Daily Practice</h3>
              <p className="text-muted-foreground text-sm">
                Streak tracking and warm-up exercises keep your comedy muscles sharp. Coming soon.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 spotlight-gradient">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase tracking-tight mb-6">
            Ready to <span className="gradient-text">Kill</span>?
          </h2>
          <p className="text-muted-foreground mb-10">
            Join comedians who are writing smarter, not harder.
          </p>
          <Button 
            onClick={handleLogin}
            size="lg"
            data-testid="cta-get-started-btn"
            className="bg-primary text-primary-foreground font-bold uppercase tracking-wider text-lg px-8 py-6 hover:bg-spotlight-hover spotlight-glow btn-lift"
          >
            Start Writing Free
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Mic className="w-5 h-5 text-primary" />
            <span className="font-display text-sm uppercase tracking-wider">BitStack</span>
          </div>
          <p className="text-muted-foreground text-sm">
            © 2025 BitStack. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
